// Card #3614 -- the scribe's bell-to-entry countdown on the public boards.
//
// The server (score-api, `data.entryTimer`) sends the timer only while one is
// running or paused on the ride the board shows: { rideId, running, limitSeconds,
// remainingMs } with remainingMs measured AS OF the payload's serverTime (it can be
// negative once the limit passed; frozen when `running` is false). The board never
// trusts a wall clock for this: score-fe's api layer stamps the entry, when the
// response lands, with `receivedAtMs` (performance.now(), monotonic) and `ageMs`
// (the response's Age header: seconds the phone path's edge cache held the copy,
// 0 on the uncached venue path). From those the live value is
//
//   remainingMs - ageMs - (now - receivedAtMs)        while running
//   remainingMs                                       while paused (frozen)
//
// The board shows tenths, clamped at 0.0, and blinks at 0.0. Everything here is
// defensive: a malformed entry yields null, which renders nothing. The board never
// shows an error (scoreboard doctrine), so this never throws.

const finite = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// Milliseconds left right now, or null when there is no usable timer.
export function entryTimerRemainingMs(entryTimer, nowMs) {
  if (!entryTimer || typeof entryTimer !== "object") return null;
  const remaining = finite(entryTimer.remainingMs);
  if (remaining === null) return null;
  if (entryTimer.running !== true) return remaining;
  const received = finite(entryTimer.receivedAtMs);
  const now = finite(nowMs);
  if (received === null || now === null) return remaining;
  const age = Math.max(0, finite(entryTimer.ageMs) ?? 0);
  return remaining - age - Math.max(0, now - received);
}

// "12.3". Rounds UP to the next tenth, so 0.0 appears only once time is truly
// up; clamped at 0.0.
export function formatEntryTimerTenths(remainingMs) {
  const ms = finite(remainingMs);
  if (ms === null) return null;
  if (ms <= 0) return "0.0";
  return (Math.ceil(ms / 100) / 10).toFixed(1);
}

// The Age header (whole seconds, a string) as milliseconds; 0 when absent/junk.
export function ageHeaderToMs(value) {
  const n = Number(Array.isArray(value) ? value[0] : value);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1000) : 0;
}
